import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, IconButton, Collapse } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { formatText } from '../utils/formatText';
import parse from 'html-react-parser';
import theme from './../theme';
import API_BASE from '../config';
import supabase from '../supabaseClient';

const ChatWindow = () => {
  const CHAT_STORAGE_KEY = 'chat_state_v1';
  const CHAT_HISTORY_KEY = 'chat_history_v1';
  const [input, setInput] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [isSaved, setIsSaved] = useState(false); // Tracks whether current response is saved
  const [bookmarks, setBookmarks] = useState([]); // Array to store prompt/response pairs
  const [history, setHistory] = useState([]); // persistent conversation turns
  const bottomRef = useRef(null);
  const inputBarRef = useRef(null);
  const [scrollPad, setScrollPad] = useState(0);
  const EXTRA_BOTTOM_SPACE = 24; // px, small whitespace at bottom
  const [bottomOffset, setBottomOffset] = useState(0); // for on-screen keyboard
  const [sources, setSources] = useState([]); // RAG sources for current turn
  const [sourcesOpen, setSourcesOpen] = useState(false); // toggle for live sources panel
  const [historySourcesOpen, setHistorySourcesOpen] = useState({}); // per-turn toggle

  const cleanTitle = (title) => (title || 'Untitled').replace(/\.[a-z0-9]{1,5}$/i, '');
  const SourcesPanel = ({ items = [], open = false, onToggle }) => (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>Sources</Box>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={open ? 'Hide sources' : 'Show sources'}
          sx={{ fontSize: 12, p: 0.5, color: '#EAD7B2' }}
        >
          {open ? '\u25BC' : '\u25B6'}
        </IconButton>
      </Box>
      <Collapse
        in={open}
        timeout={{ enter: 480, exit: 360 }}
        easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        unmountOnExit
      >
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          {items.map((s, i) => (
            <Box component="li" key={i} sx={{ fontSize: 12, color: 'text.secondary' }}>
              {s.source ? (
                <a href={s.source} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                  {cleanTitle(s.title)}
                </a>
              ) : (
                <span>{cleanTitle(s.title)}</span>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );

  // Load bookmarks and history from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('bookmarks');
    if (stored) {
      setBookmarks(JSON.parse(stored));
    }
    const histRaw = localStorage.getItem(CHAT_HISTORY_KEY);
    if (histRaw) {
      try { setHistory(JSON.parse(histRaw)); } catch (_) {}
    }
  }, []);

  // Always scroll to bottom on load and when content updates.
  useEffect(() => {
    const id = setTimeout(() => {
      if (!bottomRef.current) return;
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 0);
    return () => clearTimeout(id);
  }, [history, response, loading, scrollPad]);

  // Recompute scroll padding when the input bar size/position may change
  useEffect(() => {
    const pad = (inputBarRef.current?.offsetHeight || 0) + 16 + EXTRA_BOTTOM_SPACE; // add small bottom whitespace
    setScrollPad(pad);
  }, [bottomOffset, loading, input]);

  // Hydrate history from backend context as a server-side source of truth,
  // so the conversation persists even if local component state resets.
  useEffect(() => {
    // Only hydrate if we don't already have history
    if (history && history.length > 0) return;
    (async () => {
      try {
        // Include auth so backend can scope to the signed-in user
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token || '';
        const res = await axios.get(`${API_BASE}/context`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        const data = Array.isArray(res.data) ? res.data : [];

        // Accept either shape:
        // - turns: [{ prompt, response, created_at }]
        // - messages: [{ role: 'user'|'assistant', content }]
        let turns = [];
        if (data.length && (data[0]?.prompt !== undefined || data[0]?.response !== undefined)) {
          // turns shape
          turns = data
            .filter(t => t && (t.prompt || t.response))
            .map(t => ({
              prompt: t.prompt || '',
              response: formatText(t.response || ''),
              sources: Array.isArray(t.sources) ? t.sources : [],
              timestamp: t.created_at || new Date().toISOString(),
            }));
        } else {
          // messages shape
          for (let i = 0; i < data.length; i++) {
            const m = data[i];
            if (m?.role === 'user') {
              const next = data[i + 1];
              if (next && next.role === 'assistant') {
                turns.push({
                  prompt: m.content || '',
                  response: formatText(next.content || ''),
                  timestamp: new Date().toISOString(),
                });
                i++; // skip matched assistant
              }
            }
          }
        }
        if (turns.length) setHistory(turns);
      } catch (e) {
        // non-fatal if context isn't available
        console.warn('Failed to hydrate from /context', e);
      }
    })();
  }, [history]);

  // (Optional) Persist a lightweight snapshot only while loading, so unfinished streams can be restored later
  useEffect(() => {
    if (loading) {
      const toSave = { response, userPrompt, isSaved };
      try { localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(toSave)); } catch (_) {}
    }
  }, [loading, response, userPrompt, isSaved]);

  // Persist history whenever it changes
  useEffect(() => {
    try { localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(history)); } catch (_) {}
  }, [history]);

  // Track visual viewport changes: ignore small chrome jiggle, throttle with rAF,
  // and avoid listening to visualViewport.scroll to reduce flicker.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let raf = 0;
    const THRESHOLD = 120; // px; treat smaller changes as not a keyboard event

    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const gap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setBottomOffset(gap > THRESHOLD ? gap : 0);
        const pad = (inputBarRef.current?.offsetHeight || 0) + 16; // small buffer
        setScrollPad(pad);
      });
    };

    update();
    vv.addEventListener('resize', update);
    // Intentionally do NOT listen to vv.scroll to avoid jitter

    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener('resize', update);
    };
  }, []);

  const handleSend = async () => {
    if (!input.trim()) return;

    setResponse('');
    setLoading(true);
    setSources([]);
    let receivedSources = [];
    setSourcesOpen(false);
    const currentPrompt = input;
    setUserPrompt(currentPrompt); // Store the current prompt
    setIsSaved(false); // Reset the "Save" button on new prompt
    setInput(''); // Clear input field immediately

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token || '';
      const resp = await fetch(`${API_BASE}/chat-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ message: input })
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.type === 'sources' && Array.isArray(evt.items)) {
              receivedSources = evt.items;
              setSources(evt.items);
              continue;
            }
            if (evt.type === 'content' && typeof evt.text === 'string') {
              if (loading) setLoading(false);
              accumulated += evt.text;
              setResponse(formatText(accumulated));
            } else if (evt.type === 'error') {
              if (loading) setLoading(false);
              throw new Error(evt.message || 'Stream error');
            } else if (evt.type === 'done') {
              if (loading) setLoading(false);
              // finished
            }
          } catch (e) {
            console.error('Stream parse error:', e);
          }
        }
      }
      // After stream completes, append this turn to history and clear current turn
      const finalResponse = formatText(accumulated);
      // Append the new turn; keep its Sources panel collapsed by default
      setHistory((prev) => {
        const idx = prev.length; // index of the new turn
        const turn = {
          prompt: currentPrompt,
          response: finalResponse,
          sources: Array.isArray(receivedSources) ? [...receivedSources] : [],
          timestamp: new Date().toISOString(),
        };
        // Do not auto-expand; leave collapsed until user toggles
        // setHistorySourcesOpen((prevOpen) => ({ ...prevOpen, [idx]: true }));
        return [...prev, turn];
      });
      setUserPrompt('');
      setResponse('');
      setIsSaved(false);
      try { localStorage.removeItem(CHAT_STORAGE_KEY); } catch (_) {}
    } catch (error) {
      console.error('Error fetching response:', error);
      setResponse('Error: Unable to get response.');
    }
    setLoading(false);
  };

  const handleSave = () => {
    // Choose the most recent turn: current response if present, else last history item
    let toSave = null;
    if (response && response.trim().length) {
      toSave = { prompt: userPrompt, response };
    } else if (history.length > 0) {
      const last = history[history.length - 1];
      if (last && last.response) {
        toSave = { prompt: last.prompt, response: last.response };
      }
    }
    if (toSave && !isSaved) {
      const newPair = { ...toSave, timestamp: new Date().toISOString() };
      const updatedBookmarks = [...bookmarks, newPair];
      setBookmarks(updatedBookmarks);
      localStorage.setItem('bookmarks', JSON.stringify(updatedBookmarks));
      setIsSaved(true);
    }
  };

  return (
    <>
    <Box sx={{ p: 2, mt: 2, pb: { xs: 10, sm: 10 }, borderRadius: 2, bgcolor: theme.palette.background.default, boxShadow: 0 }}>
      {/* Display the User Prompt */}
      {/* Render persisted conversation history */}
      {history && history.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {history.map((t, i) => (
            <Box key={i}>
              {/* User bubble (right aligned) */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                <Box
                  sx={{
                    bgcolor: '#A7CF83',
                    color: '#000000',
                    px: 1.5,
                    py: 1,
                    borderRadius: 5,
                    borderTopRightRadius: 0,
                    maxWidth: '80%',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {t.prompt}
                </Box>
              </Box>
              {/* Sources for this turn (if any). Skip latest turn — shown by the unified panel below. */}
              {i !== history.length - 1 && Array.isArray(t.sources) && t.sources.length > 0 && (
                <SourcesPanel
                  items={t.sources}
                  open={!!historySourcesOpen[i]}
                  onToggle={() => setHistorySourcesOpen((prev) => ({ ...prev, [i]: !prev[i] }))}
                />
              )}
              {/* Assistant answer (left aligned) */}
              <Box sx={{ whiteSpace: 'pre-wrap' }}>{parse(t.response)}</Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Current turn (may be streaming) */}
      {userPrompt && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box
            sx={{
              bgcolor: '#A7CF83',
              color: '#000000',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              borderTopRightRadius: 0,
              maxWidth: '80%',
              whiteSpace: 'pre-wrap',
            }}
          >
            {userPrompt}
          </Box>
        </Box>
      )}

      {/* Unified Sources panel: show live during streaming, else show latest saved turn's sources */}
      {(() => {
        const lastIdx = history.length - 1;
        const latestSaved = lastIdx >= 0 && Array.isArray(history[lastIdx]?.sources) ? history[lastIdx].sources : [];
        const items = (userPrompt && sources.length > 0) ? sources : latestSaved;
        if (!items || items.length === 0) return null;
        const open = userPrompt ? sourcesOpen : !!historySourcesOpen[lastIdx];
        const onToggle = userPrompt
          ? () => setSourcesOpen(o => { const next = !o; if (next) setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, 10); return next; })
          : () => setHistorySourcesOpen(prev => ({ ...prev, [lastIdx]: !prev[lastIdx] }));
        return <SourcesPanel items={items} open={open} onToggle={onToggle} />;
      })()}

      {/* Display the Response (only when present or loading) */}
      {(response || loading) && (
        <Box
          sx={{
            whiteSpace: 'pre-wrap',
            // Remove trailing bottom margin from generated HTML (p, ul, etc.)
            '& :last-child': { marginBottom: 0 },
          }}
        >
          {parse(response)}
          {loading && (
            <span style={{ opacity: 0.6 }}> typing…</span>
          )}
        </Box>
      )}

      {/* Save button moved to bottom input bar */}
      {/* Anchor for auto-scroll to bottom with margin to account for fixed input */}
      <Box ref={bottomRef} sx={{ height: `${EXTRA_BOTTOM_SPACE}px`, scrollMarginBottom: `${scrollPad}px` }} />
    </Box>

    {/* Fixed bottom input bar (content scrolls behind it) */}
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: `${bottomOffset}px`,
        zIndex: 1100,
        borderTop: '0.25px solid #EAD7B2',
        bgcolor: 'background.paper',
        pb: 'calc(env(safe-area-inset-bottom) + 12px)',
      }}
    >
      <Box
        ref={inputBarRef}
        sx={{
          maxWidth: { xs: '100%', sm: 720 },
          mx: 'auto',
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question..."
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <IconButton
          onClick={handleSend}
          color="inherit"
          sx={{
            bgcolor: '#A7CF83',
            color: '#000000',
            borderRadius: '50%',
            '&:hover': { bgcolor: '#97bf73' },
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
        <IconButton
          aria-label={isSaved ? 'Saved' : 'Save'}
          title={isSaved ? 'Saved' : 'Save'}
          onClick={handleSave}
          color="inherit"
          disabled={(() => {
            const hasCurrent = !!(response && response.trim().length);
            const hasHistory = history.length > 0 && !!(history[history.length - 1]?.response);
            return !(hasCurrent || hasHistory) || isSaved;
          })()}
          sx={{
            bgcolor: '#A7CF83',
            color: '#000000',
            borderRadius: '50%',
            '&:hover': { bgcolor: '#97bf73' },
          }}
        >
          <SaveIcon />
        </IconButton>
      </Box>
    </Box>
    </>
  );
};

export default ChatWindow;
